import { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { Button, Checkbox, cn, Dialog, DialogContent } from '@proerp/ui';
import { useCurrentUser, useUpdateNavPreferences } from '@/lib/useCurrentUser';
import { useToasts } from '@/lib/toast';
import { NAV_GROUPS, resolveNavGroupOrder, type NavGroup } from './nav';

/**
 * Module ein-/ausblenden und in eigener Reihenfolge sortieren — reine Anzeige-
 * Präferenz am eigenen Benutzerkonto (gilt geräteübergreifend, siehe
 * apps/api/src/modules/auth/auth.service.ts `updateNavPreferences`). Blendet
 * nur Sidebar/Bottom-Navigation aus bzw. ordnet sie um — direkter Aufruf einer
 * Seite und die Suche (⌘K) funktionieren unverändert.
 */
export function ModuleVisibilityDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const push = useToasts((state) => state.push);
  const { data } = useCurrentUser();
  const update = useUpdateNavPreferences();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<NavGroup[]>(NAV_GROUPS as unknown as NavGroup[]);

  // Pointer-Drag: gezogene Gruppe + aktuelle Ziel-Position (Muster ProjectTasksTab).
  const [dragGroup, setDragGroup] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const overIndexRef = useRef<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    setHidden(new Set(data?.data.hiddenNavGroups ?? []));
    setOrder(resolveNavGroupOrder(data?.data.navGroupOrder ?? []));
  }, [open, data]);

  function toggle(group: string, visible: boolean) {
    setHidden((current) => {
      const next = new Set(current);
      if (visible) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  function onGripPointerDown(event: React.PointerEvent, group: NavGroup) {
    event.preventDefault();
    setDragGroup(group);
    const startIndex = order.indexOf(group);
    overIndexRef.current = startIndex;
    setOverIndex(startIndex);
    const onMove = (move: PointerEvent) => {
      const rows = [...(listRef.current?.querySelectorAll('[data-group-row]') ?? [])] as HTMLElement[];
      const index = rows.findIndex((row) => {
        const rect = row.getBoundingClientRect();
        return move.clientY < rect.top + rect.height / 2;
      });
      const next = index === -1 ? rows.length : index;
      overIndexRef.current = next;
      setOverIndex(next);
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
    const onUp = () => {
      cleanup();
      const target = overIndexRef.current;
      setDragGroup(null);
      setOverIndex(null);
      if (target === null) return;
      const from = order.indexOf(group);
      let to = target > from ? target - 1 : target;
      to = Math.max(0, Math.min(order.length - 1, to));
      if (from !== -1 && from !== to) {
        setOrder((current) => {
          const next = [...current];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          return next;
        });
      }
    };
    const onCancel = () => {
      cleanup();
      setDragGroup(null);
      setOverIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }

  async function save() {
    try {
      await update.mutateAsync({ hiddenGroups: [...hidden], groupOrder: order });
      push('success', 'Gespeichert.');
      onOpenChange(false);
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Module verwalten"
        description="Wähle, welche Bereiche in deiner Navigation angezeigt werden, und ziehe sie in deine gewünschte Reihenfolge. Wirkt nur bei dir — direkter Aufruf und die Suche bleiben davon unberührt."
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" isLoading={update.isPending} onClick={save}>
              Speichern
            </Button>
          </>
        }
      >
        <ul ref={listRef} className="flex flex-col gap-1">
          {order.map((group, index) => (
            <li
              key={group}
              data-group-row
              className={cn(
                'flex items-center gap-2 rounded-md px-1 py-1 text-sm text-foreground',
                dragGroup === group && 'opacity-50',
                overIndex === index && dragGroup && dragGroup !== group && 'border-t-2 border-t-primary',
              )}
            >
              <button
                type="button"
                className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
                onPointerDown={(event) => onGripPointerDown(event, group)}
                aria-label={`${group} verschieben`}
                title="Verschieben"
              >
                <GripVertical className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <label className="flex flex-1 items-center gap-2">
                <Checkbox
                  checked={!hidden.has(group)}
                  onCheckedChange={(checked) => toggle(group, checked === true)}
                />
                {group}
              </label>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
